/**
 * Center panel: activities of selected step, add (picker), delete, DnD, selection.
 */

import { useState } from "react";
import { Plus, Trash2, GripVertical } from "lucide-react";
import { Button } from "../../primitives/Button";
import type { ProcessActivityTemplateStudioOut } from "../../../api/opsV2/types";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  studioAddActivity,
  studioDeleteActivity,
} from "../../../api/opsV2/client";
import { getCurrentOrgId } from "../../../state/orgContext";
import { ActivityTypePickerModal } from "./ActivityTypePickerModal";

type ActivitiesPanelProps = {
  activities: ProcessActivityTemplateStudioOut[];
  selectedActivityId: string | null;
  onSelectActivity: (id: string | null) => void;
  onActivitiesChange: (activities: ProcessActivityTemplateStudioOut[]) => void;
  stepId: string | null;
  templateId: string;
};

function ActivityRow({
  activity,
  isSelected,
  onSelect,
  onDelete,
}: {
  activity: ProcessActivityTemplateStudioOut;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: activity.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 p-2 rounded border cursor-pointer group ${
        isSelected ? "border-primary bg-primary/5" : "border-border hover:bg-muted/30"
      } ${isDragging ? "opacity-50" : ""}`}
      onClick={onSelect}
    >
      <button type="button" className="touch-none cursor-grab active:cursor-grabbing p-0.5 text-muted" {...attributes} {...listeners} onClick={(e) => e.stopPropagation()}>
        <GripVertical size={14} />
      </button>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm truncate">{activity.title}</div>
        <div className="text-xs text-muted">{activity.activity_type}</div>
      </div>
      <button
        type="button"
        className="opacity-0 group-hover:opacity-100 p-1 text-muted hover:text-destructive"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        aria-label="Supprimer"
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}

export function ActivitiesPanel({
  activities,
  selectedActivityId,
  onSelectActivity,
  onActivitiesChange,
  stepId,
  templateId,
}: ActivitiesPanelProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [adding, setAdding] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = activities.findIndex((a) => a.id === active.id);
    const newIndex = activities.findIndex((a) => a.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    onActivitiesChange(arrayMove(activities, oldIndex, newIndex));
  };

  const handleAddActivity = (activityType: string, _handlerVersion: number) => {
    if (!stepId || !templateId) return;
    setAdding(true);
    const orgId = getCurrentOrgId();
    studioAddActivity(
      templateId,
      stepId,
      {
        title: activityType,
        activity_type: activityType,
        is_required: true,
        description: null,
        activity_ui_config: {},
      },
      orgId
    )
      .then((created) => {
        onActivitiesChange([...activities, created as unknown as ProcessActivityTemplateStudioOut]);
        onSelectActivity(created.id);
      })
      .catch((e: unknown) => alert(e instanceof Error ? e.message : "Erreur ajout activité"))
      .finally(() => {
        setAdding(false);
        setPickerOpen(false);
      });
  };

  const handleDeleteActivity = async (activityId: string) => {
    if (!confirm("Supprimer cette activité ?") || !templateId) return;
    try {
      const orgId = getCurrentOrgId();
      await studioDeleteActivity(templateId, activityId, orgId);
      onActivitiesChange(activities.filter((a) => a.id !== activityId));
      if (selectedActivityId === activityId) onSelectActivity(activities[0]?.id ?? null);
    } catch (e) {
      alert(e instanceof Error ? e.message : "Erreur suppression");
    }
  };

  return (
    <div className="p-2 flex flex-col h-full">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-text">Activités</h3>
        <Button
          label="Ajouter"
          intent="ghost"
          size="sm"
          onClick={() => setPickerOpen(true)}
          state={adding ? "loading" : "default"}
          disabled={!stepId}
        />
      </div>
      {!stepId ? (
        <p className="text-xs text-muted py-2">Sélectionnez une étape à gauche.</p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={activities.map((a) => a.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-1 overflow-auto flex-1">
              {activities.length === 0 && <p className="text-xs text-muted py-2">Aucune activité. Cliquez sur Ajouter.</p>}
              {activities.map((activity) => (
                <ActivityRow
                  key={activity.id}
                  activity={activity}
                  isSelected={selectedActivityId === activity.id}
                  onSelect={() => onSelectActivity(activity.id)}
                  onDelete={() => handleDeleteActivity(activity.id)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
      <ActivityTypePickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handleAddActivity}
      />
    </div>
  );
}
